import { useState, useEffect, useRef, useCallback } from 'react';
import jsQR from 'jsqr';
import api, { dataAPI } from '../services/api';
import secureStorage from '../services/secureStorage';

/**
 * Lógica de datos de los códigos 2FA (TOTP/HOTP), extraída de
 * components/OTPManager.js para que la pantalla nueva (pages/TwoFactor.js)
 * la reutilice sin duplicar el parser ni el guardado. OTPManager.js no se
 * toca: sigue con su propia copia interna de todo esto, funcionando igual
 * que antes.
 *
 * El vault de OTP vive como un ítem normal del vault (`__otp_entries__`,
 * `data.type === 'otp_vault'`), cifrado igual que cualquier otro dato.
 */

// --- TOTP/HOTP: mismo algoritmo que OTPManager.js -----------------------

const base32Decode = (input) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let bits = '';
  const output = [];
  const cleanInput = input.toUpperCase().replace(/[^A-Z2-7]/g, '');
  for (let i = 0; i < cleanInput.length; i++) {
    const val = alphabet.indexOf(cleanInput[i]);
    if (val === -1) continue;
    bits += val.toString(2).padStart(5, '0');
  }
  for (let i = 0; i + 8 <= bits.length; i += 8) {
    output.push(parseInt(bits.substr(i, 8), 2));
  }
  return new Uint8Array(output);
};

const bytesToBase32 = (bytes) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let bits = '';
  let result = '';
  for (let i = 0; i < bytes.length; i++) bits += bytes[i].toString(2).padStart(8, '0');
  for (let i = 0; i + 5 <= bits.length; i += 5) result += alphabet[parseInt(bits.substr(i, 5), 2)];
  while (result.length % 8 !== 0) result += '=';
  return result;
};

const hmacSha1 = async (key, message) => {
  const cryptoKey = await crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, message);
  return new Uint8Array(signature);
};

export const generateTOTP = async (secret, period = 30, digits = 6) => {
  try {
    const key = base32Decode(secret);
    const time = Math.floor(Date.now() / 1000 / period);
    const timeBuffer = new ArrayBuffer(8);
    const timeView = new DataView(timeBuffer);
    timeView.setUint32(4, time, false);
    const hmac = await hmacSha1(key, new Uint8Array(timeBuffer));
    const offset = hmac[hmac.length - 1] & 0x0f;
    const code = (
      ((hmac[offset] & 0x7f) << 24) |
      ((hmac[offset + 1] & 0xff) << 16) |
      ((hmac[offset + 2] & 0xff) << 8) |
      (hmac[offset + 3] & 0xff)
    ) % Math.pow(10, digits);
    return code.toString().padStart(digits, '0');
  } catch (e) {
    console.error('Error generating TOTP:', e);
    return null;
  }
};

export const parseOtpAuthUrl = (url) => {
  try {
    const match = url.match(/^otpauth:\/\/(totp|hotp)\/([^?]+)\?(.+)$/i);
    if (!match) return null;
    const type = match[1].toLowerCase();
    const label = decodeURIComponent(match[2]);
    const params = new URLSearchParams(match[3]);
    const [issuer, account] = label.includes(':')
      ? label.split(':').map((s) => s.trim())
      : [params.get('issuer') || '', label];
    return {
      type,
      issuer: params.get('issuer') || issuer,
      account,
      secret: params.get('secret'),
      algorithm: params.get('algorithm') || 'SHA1',
      digits: parseInt(params.get('digits'), 10) || 6,
      period: parseInt(params.get('period'), 10) || 30,
      counter: parseInt(params.get('counter'), 10) || 0
    };
  } catch (e) {
    return null;
  }
};

// Formato de exportación de Google Authenticator (otpauth-migration://offline?data=...)
export const parseGoogleAuthMigration = (url) => {
  try {
    if (!url.startsWith('otpauth-migration://')) return null;
    const dataMatch = url.match(/data=([^&]+)/);
    if (!dataMatch) return null;
    const base64Data = decodeURIComponent(dataMatch[1]);
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);

    const entries = [];
    let pos = 0;
    while (pos < bytes.length) {
      if (bytes[pos] === 0x0a) {
        pos++;
        const len = bytes[pos++];
        const entryEnd = pos + len;
        const entry = { secret: '', issuer: '', account: '', type: 'totp', digits: 6, algorithm: 'SHA1' };
        while (pos < entryEnd) {
          const fieldTag = bytes[pos++];
          const fieldNum = fieldTag >> 3;
          const wireType = fieldTag & 0x07;
          if (wireType === 2) {
            const fieldLen = bytes[pos++];
            const fieldData = bytes.slice(pos, pos + fieldLen);
            pos += fieldLen;
            if (fieldNum === 1) entry.secret = bytesToBase32(fieldData);
            else if (fieldNum === 2) entry.account = new TextDecoder().decode(fieldData);
            else if (fieldNum === 3) entry.issuer = new TextDecoder().decode(fieldData);
          } else if (wireType === 0) {
            let value = 0;
            let shift = 0;
            while (bytes[pos] & 0x80) {
              value |= (bytes[pos++] & 0x7f) << shift;
              shift += 7;
            }
            value |= bytes[pos++] << shift;
            if (fieldNum === 4) entry.algorithm = value === 2 ? 'SHA256' : value === 3 ? 'SHA512' : 'SHA1';
            else if (fieldNum === 5) entry.digits = value === 2 ? 8 : 6;
            else if (fieldNum === 6) entry.type = value === 1 ? 'hotp' : 'totp';
          }
        }
        if (entry.secret) {
          if (entry.account.includes(':')) {
            const parts = entry.account.split(':');
            if (!entry.issuer) entry.issuer = parts[0].trim();
            entry.account = parts[1].trim();
          }
          entries.push(entry);
        }
      } else {
        pos++;
      }
    }
    return entries;
  } catch (e) {
    console.error('Error parsing Google Auth migration:', e);
    return null;
  }
};

/**
 * Decodifica el primer QR de un fichero de imagen (foto o captura). Se usa
 * tanto para "añadir cuenta" como para "importar" — mismo `jsQR` que
 * OTPManager.js, sobre un `<canvas>` fuera de pantalla.
 */
export function decodeQrFromImageFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        resolve(code?.data || null);
      };
      img.onerror = () => reject(new Error('No se pudo leer la imagen'));
      img.src = event.target.result;
    };
    reader.onerror = () => reject(new Error('No se pudo leer el fichero'));
    reader.readAsDataURL(file);
  });
}

/** Un texto de QR (otpauth:// u otpauth-migration://) a una o varias entradas sin id/fecha. */
export function parseAnyOtpQr(qrText) {
  if (!qrText) return [];
  const migration = parseGoogleAuthMigration(qrText);
  if (migration && migration.length > 0) return migration;
  const single = parseOtpAuthUrl(qrText);
  return single ? [single] : [];
}

const normalizeOtpEntries = (entries = []) => {
  if (!Array.isArray(entries)) return [];
  return [...entries]
    .map((entry) => ({
      id: entry.id || '',
      issuer: entry.issuer || '',
      account: entry.account || '',
      secret: entry.secret || '',
      type: entry.type || 'totp',
      digits: entry.digits || 6,
      period: entry.period || 30,
      algorithm: entry.algorithm || 'SHA1',
      counter: entry.counter || 0,
      createdAt: entry.createdAt || ''
    }))
    .sort((a, b) => (a.id || '').localeCompare(b.id || ''));
};

const buildOtpSignature = (entries = []) => JSON.stringify(normalizeOtpEntries(entries));

const OTP_CACHE_VERSION = 'v2';

/**
 * Carga/guarda el vault de OTP (`__otp_entries__`), genera los códigos cada
 * segundo y expone las acciones de alta/baja. Mismo contrato de caché
 * (secureStorage, origin-bound) y de sincronización que OTPManager.js.
 */
export function useOtpEntries() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState({});
  const [timeLeft, setTimeLeft] = useState(30);

  const otpDbIdRef = useRef(null);
  const loadedRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  const isDirtyRef = useRef(false);
  const lastSavedSignatureRef = useRef('[]');
  const hasCodesRef = useRef(false);

  const persistCache = useCallback(async (list, metadata = {}) => {
    if (!secureStorage.isReady()) return;
    await secureStorage.setItem('otp_entries_cache', list);
    await secureStorage.setItem('otp_entries_cache_timestamp', Date.now());
    await secureStorage.setItem('otp_entries_cache_version', OTP_CACHE_VERSION);
    if (metadata.itemId) await secureStorage.setItem('otp_entries_item_id', metadata.itemId);
    if (metadata.itemUpdatedAt) await secureStorage.setItem('otp_entries_item_updated_at', metadata.itemUpdatedAt);
  }, []);

  const clearCache = useCallback(async () => {
    if (!secureStorage.isReady()) return;
    secureStorage.removeItem('otp_entries_cache');
    secureStorage.removeItem('otp_entries_cache_timestamp');
    secureStorage.removeItem('otp_entries_cache_version');
    secureStorage.removeItem('otp_entries_item_id');
    secureStorage.removeItem('otp_entries_item_updated_at');
  }, []);

  const isOtpItem = (item) => item && (item.name === '__otp_entries__' || item.data?.type === 'otp_vault');

  const load = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);

    let cached = null;
    try {
      if (secureStorage.isReady()) {
        const version = await secureStorage.getItem('otp_entries_cache_version');
        if (version && version !== OTP_CACHE_VERSION) {
          await clearCache();
        } else {
          const cachedEntries = await secureStorage.getItem('otp_entries_cache');
          const cachedItemId = await secureStorage.getItem('otp_entries_item_id');
          if (Array.isArray(cachedEntries)) {
            cached = cachedEntries;
            otpDbIdRef.current = cachedItemId || null;
            setEntries(cachedEntries);
            lastSavedSignatureRef.current = buildOtpSignature(cachedEntries);
          }
        }
      }
    } catch (e) {
      console.warn('Error loading OTP cache:', e);
    }

    const rfidId = localStorage.getItem('rfidId');
    if (!rfidId) { setLoading(false); return; }

    try {
      const res = await api.get(`/data/${rfidId}`, { params: { light: '1', search: '__otp_entries__', limit: 10 } });
      const found = (res.data?.data || []).find(isOtpItem);

      if (!found) {
        otpDbIdRef.current = null;
        if (!Array.isArray(cached) || cached.length === 0) {
          setEntries([]);
          lastSavedSignatureRef.current = '[]';
        }
        await clearCache();
        setLoading(false);
        return;
      }

      const detail = await dataAPI.get(found.id || found._id, rfidId);
      const parsed = typeof detail.data?.data === 'string' ? JSON.parse(detail.data.data) : (detail.data?.data || {});
      const list = Array.isArray(parsed.entries) ? parsed.entries : [];

      otpDbIdRef.current = found.id || found._id;
      setEntries(list);
      lastSavedSignatureRef.current = buildOtpSignature(list);
      await persistCache(list, { itemId: otpDbIdRef.current, itemUpdatedAt: detail.data?.updatedAt || null });
    } catch (e) {
      console.error('Error loading OTP entries:', e);
    } finally {
      setLoading(false);
    }
  }, [clearCache, persistCache]);

  useEffect(() => { load(); }, [load]);

  const saveToDB = useCallback(async (list) => {
    const rfidId = localStorage.getItem('rfidId');
    if (!rfidId) return;

    if (!Array.isArray(list) || list.length === 0) {
      if (otpDbIdRef.current) {
        try {
          await dataAPI.delete(otpDbIdRef.current, rfidId);
        } catch (e) {
          console.error('Error deleting OTP record:', e);
        }
        otpDbIdRef.current = null;
      }
      await clearCache();
      return;
    }

    try {
      const res = await dataAPI.save({
        rfidId,
        name: '__otp_entries__',
        description: 'OTP Authenticator entries (encrypted)',
        data: { type: 'otp_vault', entries: list },
        tags: ['otp', 'system'],
        ...(otpDbIdRef.current ? { itemId: otpDbIdRef.current } : {})
      });

      if (!otpDbIdRef.current) {
        const listRes = await api.get(`/data/${rfidId}`, { params: { light: '1', search: '__otp_entries__', limit: 10 } });
        const found = (listRes.data?.data || []).find(isOtpItem);
        if (found) otpDbIdRef.current = found.id || found._id;
      }

      await persistCache(list, { itemId: otpDbIdRef.current, itemUpdatedAt: res?.data?.updatedAt || null });
    } catch (e) {
      console.error('Error saving OTP entries:', e);
    }
  }, [clearCache, persistCache]);

  // Guarda con debounce solo cuando el cambio lo hizo el usuario (no la carga inicial)
  useEffect(() => {
    const signature = buildOtpSignature(entries);
    if (isDirtyRef.current && signature !== lastSavedSignatureRef.current) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        await saveToDB(entries);
        lastSavedSignatureRef.current = buildOtpSignature(entries);
        isDirtyRef.current = false;
      }, 600);
    }
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [entries, saveToDB]);

  // Códigos TOTP, refrescados cada segundo. Ver el comentario largo en
  // OTPManager.js sobre por qué `codes` no entra en las deps del timer.
  const generateAllCodes = useCallback(async () => {
    const newCodes = {};
    for (const entry of entries) {
      if (entry.type === 'totp') {
        const code = await generateTOTP(entry.secret, entry.period, entry.digits);
        if (code) newCodes[entry.id] = code;
      }
    }
    hasCodesRef.current = Object.keys(newCodes).length > 0;
    setCodes((prev) => (Object.keys(prev).length === 0 && !hasCodesRef.current ? prev : newCodes));
  }, [entries]);

  useEffect(() => {
    const updateTimer = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = 30 - (now % 30);
      setTimeLeft(remaining);
      if (remaining === 30 || !hasCodesRef.current) generateAllCodes();
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [generateAllCodes]);

  const addEntries = useCallback((parsedList) => {
    const now = new Date().toISOString();
    const withIds = parsedList.map((entry, i) => ({
      id: `${Date.now()}${i}${Math.random().toString(36).slice(2, 9)}`,
      ...entry,
      createdAt: now
    }));
    isDirtyRef.current = true;
    setEntries((prev) => [...prev, ...withIds]);
  }, []);

  const addEntry = useCallback((entry) => addEntries([entry]), [addEntries]);

  const deleteEntry = useCallback((id) => {
    isDirtyRef.current = true;
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  return { entries, loading, codes, timeLeft, addEntry, addEntries, deleteEntry };
}

export default useOtpEntries;
